/**
 * Pure recipe-template rendering helpers for stock-solution detail cards.
 *
 * Kept free of React/DOM so the `{amount}`/`{volume}` substitution logic can be
 * unit-tested directly. The caller is responsible for sanitising the returned
 * HTML (e.g. via DOMPurify) before embedding it in the DOM.
 */
import type { Recipe } from '@reefapp/shared';

/**
 * Human-readable label for a stock-solution volume key (mL as a string).
 * Mirrors the volume-selector dropdown so the recipe sentence echoes the
 * operator's chosen final volume rather than a hardcoded "1 L".
 */
export function volumeLabel(vol: string): string {
  switch (vol) {
    case '100':
      return '100 mL';
    case '250':
      return '250 mL';
    case '500':
      return '500 mL';
    case '1000':
      return '1 Liter';
    case '3785':
      return '1 US Gallon';
    default:
      return `${vol} mL`;
  }
}

/**
 * Render a recipe template to HTML, substituting the `{amount}` and `{volume}`
 * tokens for the selected volume. Returns the raw template unchanged when the
 * recipe has no amounts map (serial-dilution / concept-only entries) or when no
 * volume is selected.
 */
export function renderRecipeHtml(recipe: Recipe, selectedVolume: string): string {
  const template = recipe.template;
  if (!recipe.amounts || !selectedVolume) return template;

  const amount = recipe.amounts[selectedVolume];
  if (amount == null) return template;

  return template
    .replace(/\{amount\}/g, `<strong>${amount}</strong>`)
    .replace(/\{volume\}/g, volumeLabel(selectedVolume));
}
